import React from 'react';
import VersionStatsTable from './VersionStatsTable';
import TableSkeleton from './skeletons/TableSkeleton';
import { MetricLineChartCard } from './charts/MetricLineChartCard';
import { UserLineChartCard } from './charts/UserLineChartCard';
import { DashboardViewModel } from './hooks/useDashboardViewModel';
import type { DashboardChartVisibility } from './hooks/useDashboardChartVisibility';

interface DashboardPrimaryGridProps {
  loading: boolean;
  /** Versiyon tabloları: overview gelene kadar true */
  versionStatsLoading: boolean;
  overviewMetricsEmpty: boolean;
  vm: DashboardViewModel;
  visibility: DashboardChartVisibility;
}

export const DashboardPrimaryGrid: React.FC<DashboardPrimaryGridProps> = ({
  loading,
  versionStatsLoading,
  overviewMetricsEmpty,
  vm,
  visibility,
}) => {
  const {
    hiddenAppVersions,
    setHiddenAppVersions,
    hiddenUserVersions,
    setHiddenUserVersions,
    hiddenGameVersions,
    setHiddenGameVersions,
    hiddenTestVersions,
    setHiddenTestVersions,
    hiddenAllVersionsLine,
    setHiddenAllVersionsLine,
  } = visibility;

  if (loading && overviewMetricsEmpty) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TableSkeleton title="Açılan Oturum Sayısı" rows={5} columns={5} />
        <TableSkeleton title="Gelen Kullanıcı Sayısı" rows={5} columns={5} />
        <TableSkeleton title="Açılan Oyun Sayısı" rows={5} columns={5} />
        <TableSkeleton title="Açılan Test Sayısı" rows={5} columns={5} />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="space-y-6">
        <VersionStatsTable
          title="Açılan Oturum Sayısı"
          stats={vm.appSessionVersionStats}
          totalsFromStats={vm.allAppSessionVersionStats}
          loading={versionStatsLoading}
          detailLink="/opened-sessions"
        />
        <MetricLineChartCard
          title="Açılan Oturum Sayısı"
          data={vm.filteredAppSessionChartData}
          versionStatsTop={vm.appSessionVersionStats}
          allVersionsTotalCount={vm.allAppSessionVersionStats.length}
          hiddenVersions={hiddenAppVersions}
          setHiddenVersions={setHiddenAppVersions}
          allVersionsLineKey="app_session"
          hiddenAllVersionsLine={hiddenAllVersionsLine}
          setHiddenAllVersionsLine={setHiddenAllVersionsLine}
        />
      </div>

      <div className="space-y-6">
        <VersionStatsTable
          title="Gelen Kullanıcı Sayısı"
          stats={vm.userVersionStats}
          totalsFromStats={vm.allUserVersionStats}
          loading={versionStatsLoading}
          detailLink="/new-users"
        />
        <UserLineChartCard
          title="Gelen Kullanıcı Sayısı"
          data={vm.filteredUserChartData}
          chartVersions={vm.userChartTopVersions}
          allUserVersionsCount={vm.allUserVersions.length}
          hiddenSeriesKeys={hiddenUserVersions}
          setHiddenSeriesKeys={setHiddenUserVersions}
          hiddenTumLine={hiddenAllVersionsLine.has('users')}
          setHiddenAllVersionsLine={setHiddenAllVersionsLine}
        />
      </div>

      <div className="space-y-6">
        <VersionStatsTable
          title="Açılan Oyun Sayısı"
          stats={vm.gameSessionVersionStats}
          totalsFromStats={vm.allGameSessionVersionStats}
          loading={versionStatsLoading}
          detailLink="/opened-games"
        />
        <MetricLineChartCard
          title="Açılan Oyun Sayısı"
          data={vm.filteredGameSessionChartData}
          versionStatsTop={vm.gameSessionVersionStats}
          allVersionsTotalCount={vm.allGameSessionVersionStats.length}
          hiddenVersions={hiddenGameVersions}
          setHiddenVersions={setHiddenGameVersions}
          allVersionsLineKey="game_session"
          hiddenAllVersionsLine={hiddenAllVersionsLine}
          setHiddenAllVersionsLine={setHiddenAllVersionsLine}
        />
      </div>

      <div className="space-y-6">
        <VersionStatsTable
          title="Açılan Test Sayısı"
          stats={vm.testSessionVersionStats}
          totalsFromStats={vm.allTestSessionVersionStats}
          loading={versionStatsLoading}
          detailLink="/opened-tests"
        />
        <MetricLineChartCard
          title="Açılan Test Sayısı"
          data={vm.filteredTestSessionChartData}
          versionStatsTop={vm.testSessionVersionStats}
          allVersionsTotalCount={vm.allTestSessionVersionStats.length}
          hiddenVersions={hiddenTestVersions}
          setHiddenVersions={setHiddenTestVersions}
          allVersionsLineKey="test_session"
          hiddenAllVersionsLine={hiddenAllVersionsLine}
          setHiddenAllVersionsLine={setHiddenAllVersionsLine}
        />
      </div>
    </div>
  );
};
